import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { CheckCircle, ArrowRight, Phone, Factory, Users, TrendingUp } from "lucide-react";

export const TapeManufacturingPlant = () => {
  return (
    <>
      <Helmet>
        <title>Complete Tape Manufacturing Plant Setup in India - Hero Impex | Turnkey Solutions</title>
        <meta name="description" content="Complete tape manufacturing plant setup in India. Turnkey BOPP tape production line with machinery, installation, training. Project planning to production. Get quote!" />
        <meta name="keywords" content="tape manufacturing plant setup India, turnkey tape plant, complete BOPP tape production line, tape factory setup, adhesive tape plant project" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <Badge className="mb-4 bg-amber-500">Turnkey Manufacturing Solutions</Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Complete Tape Manufacturing Plant Setup in India
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                From concept to commissioning - we deliver complete turnkey tape manufacturing plants. Expert project planning, machinery supply, installation, training, and production support for BOPP tape factories across India.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-8">
                    Get Turnkey Project Proposal
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="tel:+919884012741">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900">
                    <Phone className="w-5 h-5 mr-2" />
                    Project Consultant: +91-9884012741
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">India's Leading Turnkey Tape Plant Supplier</h2>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Hero Impex India Private Limited specializes in setting up complete tape manufacturing plants with 45+ years of project execution experience. We have successfully commissioned 800+ tape manufacturing facilities across India, UAE, Bangladesh, Middle East, Africa, and other countries.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Our turnkey solutions cover every aspect - from initial feasibility study and layout design to machinery installation, operator training, and post-commissioning support. We ensure your plant is production-ready with consistent quality output.
              </p>
            </div>

            {/* Our Turnkey Services */}
            <div className="mb-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Complete Turnkey Services We Provide</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">1</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Feasibility Study</h4>
                    <p className="text-sm text-slate-600">
                      Market analysis, capacity planning, investment estimates, ROI projections
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">2</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Plant Layout Design</h4>
                    <p className="text-sm text-slate-600">
                      Space optimization, workflow planning, utility requirements, safety compliance
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">3</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Machinery Supply</h4>
                    <p className="text-sm text-slate-600">
                      Coating, slitting, rewinding, printing, and packaging machines - all from single vendor
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">4</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Installation & Commissioning</h4>
                    <p className="text-sm text-slate-600">
                      Complete installation, testing, calibration, and trial production runs
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">5</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Training Programs</h4>
                    <p className="text-sm text-slate-600">
                      Operator training, maintenance training, quality control procedures
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500 text-white rounded-lg flex items-center justify-center mb-4">
                      <span className="text-2xl font-bold">6</span>
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2">Post-Commissioning Support</h4>
                    <p className="text-sm text-slate-600">
                      Technical support, spare parts, process optimization, expansion guidance
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Plant Capacity Options */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Choose Your Plant Capacity</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="border-2 border-blue-200 hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <Factory className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                      <h3 className="text-xl font-bold text-slate-900">Small-Scale Plant</h3>
                      <p className="text-sm text-slate-600 mt-1">Perfect for Entrepreneurs</p>
                    </div>
                    <ul className="space-y-2 text-slate-700 mb-4">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Capacity:</strong> 500-1000 rolls/day</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Workers:</strong> 4-6 people</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Space:</strong> 2000-3000 sq.ft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Power:</strong> 50-75 KW</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Timeline:</strong> 90 days</span>
                      </li>
                    </ul>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700" asChild>
                      <Link to="/contact">Get Quote</Link>
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-2 border-amber-200 hover:shadow-xl transition-all relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-amber-500">Most Popular</Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <Factory className="w-12 h-12 text-amber-600 mx-auto mb-2" />
                      <h3 className="text-xl font-bold text-slate-900">Medium-Scale Plant</h3>
                      <p className="text-sm text-slate-600 mt-1">Ideal for Growing Business</p>
                    </div>
                    <ul className="space-y-2 text-slate-700 mb-4">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Capacity:</strong> 2000-4000 rolls/day</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Workers:</strong> 8-12 people</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Space:</strong> 4000-6000 sq.ft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Power:</strong> 100-150 KW</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Timeline:</strong> 120 days</span>
                      </li>
                    </ul>
                    <Button className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" asChild>
                      <Link to="/contact">Get Quote</Link>
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-2 border-green-200 hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <div className="text-center mb-4">
                      <Factory className="w-12 h-12 text-green-600 mx-auto mb-2" />
                      <h3 className="text-xl font-bold text-slate-900">Large-Scale Plant</h3>
                      <p className="text-sm text-slate-600 mt-1">For Industrial Production</p>
                    </div>
                    <ul className="space-y-2 text-slate-700 mb-4">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Capacity:</strong> 5000+ rolls/day</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Workers:</strong> 15-20 people</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Space:</strong> 8000-12000 sq.ft</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Power:</strong> 200-300 KW</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span><strong>Timeline:</strong> 150 days</span>
                      </li>
                    </ul>
                    <Button className="w-full bg-green-600 hover:bg-green-700" asChild>
                      <Link to="/contact">Get Quote</Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Why Hero Impex for Plant Setup */}
            <div className="mb-12 bg-slate-50 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose Hero Impex for Your Plant Setup?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">800+ Successful Plant Commissioning</h4>
                    <p className="text-slate-600">Proven track record across India and 20+ countries</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Single Vendor Accountability</h4>
                    <p className="text-slate-600">One point of contact for entire project - no coordination hassles</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">On-Time Project Delivery</h4>
                    <p className="text-slate-600">Guaranteed timeline adherence with penalty clauses</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Business Profitability Support</h4>
                    <p className="text-slate-600">Market guidance, pricing strategy, distribution channel advice</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Long-Term Partnership</h4>
                    <p className="text-slate-600">We grow with you - expansion support and capacity upgrades</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Pan-India Service Network</h4>
                    <p className="text-slate-600">Service engineers available across all major cities</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Timeline */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Typical Project Timeline</h2>
              <div className="space-y-4">
                {[
                  { phase: "Phase 1: Planning & Design", duration: "15-20 days", tasks: "Feasibility study, layout design, machinery selection, quotation approval" },
                  { phase: "Phase 2: Manufacturing", duration: "45-60 days", tasks: "Machine fabrication, quality testing, pre-dispatch inspection" },
                  { phase: "Phase 3: Delivery & Installation", duration: "10-15 days", tasks: "Transportation, unloading, positioning, electrical & mechanical installation" },
                  { phase: "Phase 4: Commissioning & Training", duration: "15-20 days", tasks: "Trial runs, calibration, operator training, first commercial production" },
                  { phase: "Phase 5: Handover & Support", duration: "Ongoing", tasks: "Documentation, warranty activation, technical support, spare parts supply" }
                ].map((item) => (
                  <Card key={item.phase} className="border-l-4 border-amber-500">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-bold text-slate-900 mb-1">{item.phase}</h4>
                          <p className="text-sm text-slate-600">{item.tasks}</p>
                        </div>
                        <Badge className="bg-amber-500 ml-4">{item.duration}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-10 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Let's Build Your Tape Manufacturing Plant Together</h2>
              <p className="text-xl mb-6 text-amber-50">
                Schedule a free consultation with our project team. We'll visit your location, understand your requirements, and prepare a detailed turnkey project proposal with timeline and investment breakdown.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-amber-600 hover:bg-slate-100 text-lg px-8">
                    Request Project Proposal
                  </Button>
                </Link>
                <a href="https://wa.me/919884012741?text=I want to set up a complete tape manufacturing plant. Please send me turnkey project details." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                    WhatsApp Project Team
                  </Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-amber-100">
                ✓ Free Site Visit • ✓ Detailed ROI Analysis • ✓ Transparent Pricing • ✓ 45+ Years Trust
              </p>
            </div>

          </div>
        </section>
      </div>
    </>
  );
};